import { getJobType } from 'app/description/jobs.description';
import { jobIdList } from 'app/store/state/admin/api';
import { isEqual } from 'app/utils/javascript';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import UGModal from 'shared/Custom/UGModal';
import UGTable from './UGTable';

const ViewJobsModal = ({ modal, setModal, cylinderId }) => {
    const { t } = useTranslation();
    const [jobDetails, setJobDetails] = useState({});
    const [page, setPage] = useState({ pagination: { current: 1, pageSize: 5 }, loading: false });
    const pageCursors = useRef({ 1: "" });
    const requestId = useRef(0);
    const loading = useRef(false);

    const tableColumn = [
        {
            title: t("jobId"),
            width: "30% ",
            align: "center",
            dataIndex: "jobId",
            render: (rowData) => {
                return rowData ? rowData : "-";
            },
        },
        {
            title: t("txtJobType"),
            width: "50%",
            align: "center",
            dataIndex: "jobType",
            render: (rowData) => {
                return rowData ? t(getJobType({ jobType: rowData })?.viewJobType) : "-";
            },
        },
        {
            title: t("status"),
            width: "20%",
            align: "center",
            dataIndex: "isActive",
            hidden: !isEqual(modal?.status, "ALL"),
            render: (rowData) => {
                return rowData ? t("active") : t("complete");
            },
        }
    ].filter(item => !item.hidden);

    const apiCallFn = async (pageNo = 1) => {
        if (loading.current || !Object.prototype.hasOwnProperty.call(pageCursors.current, pageNo)) return;
        const currentRequest = ++requestId.current;
        loading.current = true;
        setPage(previous => ({ ...previous, loading: true }));
        try {
            const response = await jobIdList({
                limit: 5,
                cursor: pageCursors.current[pageNo],
                status: modal?.status,
                cylinderId,
            });
            if (currentRequest !== requestId.current) return;
            if (response?.data?.data) {
                const data = response.data.data;
                const hasMore = data.nextCursor != null && data.nextCursor !== "";
                pageCursors.current = {
                    ...Object.fromEntries(Object.entries(pageCursors.current).filter(([number]) => Number(number) <= pageNo)),
                    ...(hasMore ? { [pageNo + 1]: data.nextCursor } : {}),
                };
                setJobDetails({ ...data, hasMore });
                setPage(previous => ({ ...previous, pagination: { ...previous.pagination, current: pageNo } }));
            }
        } catch (error) {
            // Keep the current page and its cursor available for a retry.
        } finally {
            if (currentRequest === requestId.current) {
                loading.current = false;
                setPage(previous => ({ ...previous, loading: false }));
            }
        }
    }

    useEffect(() => {
        pageCursors.current = { 1: "" };
        loading.current = false;
        setJobDetails({});
        setPage({ pagination: { current: 1, pageSize: 5 }, loading: false });
        if (modal?.flag) {
            apiCallFn();
        }
        return () => { requestId.current += 1; };
    }, [modal?.flag, modal?.status, cylinderId])

    const handleTableChange = (pagination) => {
        const { current } = pagination;
        apiCallFn(current)
    }

    return (
        <UGModal
            className="jobs-list-view"
            modal={modal?.flag}
            title={t("jobDetails")}
            width={isEqual(modal?.status, "ALL") ? "600px" : "450px"}
            footer={null}
            handleCancel={() => setModal({ flag: false, status: modal?.status })}
        >
            <div className="admin-table-view table-responsive px-3 py-14">
                <UGTable
                    locale={{ emptyText: t("noData") }}
                    columns={tableColumn}
                    rowKey={(record) => record?.jobId}
                    dataSource={jobDetails?.list || []}
                    pagination={{
                        ...page.pagination,
                        total: (page.pagination.current + (jobDetails.hasMore ? 1 : 0)) * page.pagination.pageSize,
                        disabled: page.loading,
                        showQuickJumper: false,
                        responsive: true,
                    }}
                    onChange={handleTableChange}
                    loading={page?.loading}
                />
            </div>
        </UGModal>
    )
}

export default ViewJobsModal
